import React, { useContext, useEffect, useMemo, useState } from 'react';
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import { GlobalContext } from "../../utils/context/GlobalContextProvider.jsx";
import { useTranslation } from "react-i18next";
import { ProjectSubscriptionApi, SubscriptionApi } from "../../api/authority/generated/index.ts";
import BillingDataForm from './Billing/BillingDataForm.jsx';
import BillingModal from "./Billing/BillingModal.jsx";
import PlanName from "./Subscription/PlanName.jsx";
import toast from "react-hot-toast";
import ConfirmationModal from "./Subscription/ConfirmationModal.jsx";
import CurrentPlanBox from "./Subscription/CurrentPlanBox.jsx";
import StripeCardModalWithElements from "./Subscription/StripeCardModal.jsx";
import {monthlyRevenueValue} from "./Subscription/subscriptionPlanPresentation.js";
import { useRenderNumber } from "../../api/utilities/useValueRenderers.js";
import LoadingComponent from "../../components/LoadingComponent.jsx";

const fetchSubscriptions = (authorityConfig, projectId) => {
    const api = new ProjectSubscriptionApi(authorityConfig);
    return api.listProjectSubscriptions({projectId});
};

const normalizePackage = (pkg) => pkg ? {...pkg, public: pkg._public ?? pkg.public ?? false} : null;

function ProjectSubscription({ project }) {
    const { getAuthorityConfiguration } = useContext(GlobalContext);
    const { t } = useTranslation();
    const [isAnnual, setIsAnnual] = useState(false);
    const [packages, setPackages] = useState([]);
    const [activePackage, setActivePackage] = useState(normalizePackage(project.subscription?.currentPackage));
    const [billingAccount, setBillingAccount] = useState(project.billingAccount);
    const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [showAllFeatures, setShowAllFeatures] = useState(false);
    const [packagesLoading, setPackagesLoading] = useState(true);
    const renderNumber = useRenderNumber();
    const currencyCode = project.currency ?? 'CZK';
    const currencySymbol = currencyCode === 'EUR' ? '€' : 'Kč';
    const monthlyPriceField = currencyCode === 'EUR' ? 'monthlyEur' : 'monthlyCzk';
    const yearlyPriceField = currencyCode === 'EUR' ? 'yearlyEur' : 'yearlyCzk';
    const toastOptions = { duration: 6000, position: 'bottom-right' };
    const monthlyRevenueLabels = {
        up_to_1m_czk: t('plan.feature_value.up_to_1m_czk', {defaultValue: 'do 1 mil. Kč'}),
        up_to_3m_czk: t('plan.feature_value.up_to_3m_czk', {defaultValue: 'do 3 mil. Kč'}),
        up_to_10m_czk: t('plan.feature_value.up_to_10m_czk', {defaultValue: 'do 10 mil. Kč'}),
    };

    const authorityConfig = useMemo(() => {
        const config = getAuthorityConfiguration();
        return config;
    }, []);

    useEffect(() => {
        if (!project?.id) return;

        let active = true;
        setPackages([]);
        setPackagesLoading(true);
        setBillingAccount(project.billingAccount);
        setSelectedPackage(null);
        setSelectedSubscription(null);
        setSetupIntentClientSecret(null);
        setIsBillingModalOpen(false);
        setIsConfirmationModalOpen(false);
        setShowAllFeatures(false);

        const fetchData = async () => {
            // Set initial subscription state
            const newActivePackage = normalizePackage(project.subscription?.currentPackage);
            if (project.subscription) {
                setIsAnnual(
                    project.subscription.nextBillingCycle
                        ? project.subscription.nextBillingCycle === 'annual'
                        : project.subscription.billingCycle === 'annual'
                );
            }
            setActivePackage(newActivePackage);

            try {
                const response = await fetchSubscriptions(authorityConfig, project.id);
                if (!active) return;
                let updatedPackages = response.map(normalizePackage);
                if (newActivePackage && !updatedPackages.some(pkg => pkg.id === newActivePackage.id)) {
                    updatedPackages.unshift(newActivePackage);
                }
                setPackages(updatedPackages);
                setPackagesLoading(false);
            } catch (error) {
                if (!active) return;
                console.error("Error fetching subscriptions:", error);
            }
        };

        fetchData();

        return () => { active = false; };
    }, [project?.id]);

    const getPrice = (pkg) => {
        return renderNumber(pkg?.[isAnnual ? yearlyPriceField : monthlyPriceField]);
    };

    const handlePaymentSuccess = () => {
        setSetupIntentClientSecret(null);
        window.location.reload();
    };

    const handleSelectPackage = (pkg) => {
        setSelectedPackage(pkg);
        setIsConfirmationModalOpen(true);
    };

    const handleConfirmSelection = () => {
        if (!project.billingAccount || !project.billingAccount.companyName) {
            setIsBillingModalOpen(true);
        } else {
            updateProjectSubscription(selectedPackage);
        }
        setIsConfirmationModalOpen(false);
    };

    const updateProjectSubscription = (pkg) => {
        const api = new SubscriptionApi(authorityConfig);
        api.changeSubscription({
            projectId: project.id,
            packageId: pkg.id,
            updateSubscriptionInput: { billingCycle: isAnnual ? 'annual' : 'monthly' }
        })
            .then(response => {
                if (response.setupIntentClientSecret !== null) {
                    setSelectedSubscription(response);
                    setSetupIntentClientSecret(response.setupIntentClientSecret);
                } else {
                    window.location.reload();
                }
            })
            .catch(error => {
                toast.error(t('subscription.change-failed', {defaultValue: 'Balíček se nepodařilo změnit.'}), toastOptions);
                console.error("Error updating subscription:", error);
            });
    };

    const renderFeatureValue = (value) => {
        if (typeof value === 'boolean') {
            return <FeatureYesNo question={value}/>;
        }
        const featureValues = {
            email: t('plan.feature_value.email', {defaultValue: 'E-mail'}),
            email_chat: t('plan.feature_value.email_chat', {defaultValue: 'E-mail a chat'}),
            priority_sla: t('plan.feature_value.priority_sla', {defaultValue: 'Prioritní SLA'}),
            '1h_quarter': t('plan.feature_value.1h_quarter', {defaultValue: '1 hodina za čtvrtletí'}),
            channel_campaign: t('plan.feature_value.channel_campaign', {defaultValue: 'Kanál / kampaň'}),
            campaign_adset_ad: t('plan.feature_value.campaign_adset_ad', {defaultValue: 'Kampaň / sada / reklama'}),
            campaign_product_order: t('plan.feature_value.campaign_product_order', {defaultValue: 'Kampaň / produkt / objednávka'}),
            ads_creatives_audiences: t('plan.feature_value.ads_creatives_audiences', {defaultValue: 'Reklamy / kreativy / publika'}),
            self_service_review: t('plan.feature_value.self_service_review', {defaultValue: 'Self-service s kontrolou'}),
            validation_included: t('plan.feature_value.validation_included', {defaultValue: 'Validační onboarding v ceně'}),
            personal: t('plan.feature_value.personal', {defaultValue: 'Osobní onboarding'}),
            video_docs: t('plan.feature_value.video_docs', {defaultValue: 'Videonávody a dokumentace'}),
            online_training_video_docs: t('plan.feature_value.online_training_video_docs', {defaultValue: '1× online školení, videonávody a dokumentace'}),
            none: t('plan.feature_value.none', {defaultValue: 'Ne'}),
        };
        const translatedValue = featureValues[value] ?? String(value);
        return <strong>{translatedValue}</strong>;
    };

    const cancelSubscription = async () => {
        if (!window.confirm(t('subscription.cancel-confirmation', {defaultValue: 'Předplatné zůstane aktivní do konce aktuálního období. Opravdu jej chcete zrušit?'}))) {
            return;
        }

        setCancelling(true);
        try {
            await new SubscriptionApi(authorityConfig).cancelSubscriptionAtPeriodEnd({projectId: project.id});
            window.location.reload();
        } catch (error) {
            toast.error(t('subscription.cancel-failed', {defaultValue: 'Předplatné se nepodařilo zrušit.'}), toastOptions);
            console.error("Error cancelling subscription:", error);
            setCancelling(false);
        }
    };

    const PackageCard = ({ packageInfo, index }) => {
        const bgColors = ['bg-gray-500', 'bg-green-500', 'bg-sky-500', 'bg-violet-500'];
        const bgColorClass = bgColors[index % bgColors.length];
        return (
            <div className={`pkg-${packageInfo.name} relative col-span-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-800 xl:col-span-4`}>
                <div className={`absolute top-0 left-0 right-0 h-0.5 ${bgColorClass}`} aria-hidden="true"></div>
                <div className="px-5 pt-5 pb-6 border-b border-gray-200 dark:border-gray-700/60">
                    <header className="flex items-center mb-2">
                        <div className={`w-6 h-6 rounded-full shrink-0 mr-3 ${bgColorClass}`}>
                            <svg className="w-6 h-6 fill-current text-white" viewBox="0 0 24 24">
                                <path d="M12 17a.833.833 0 01-.833-.833 3.333 3.333 0 00-3.334-3.334.833.833 0 110-1.666 3.333 3.333 0 003.334-3.334.833.833 0 111.666 0 3.333 3.333 0 003.334 3.334.833.833 0 110 1.666 3.333 3.333 0 00-3.334 3.334c0 .46-.373.833-.833.833z"/>
                            </svg>
                        </div>
                        <h3 className="text-lg text-gray-800 dark:text-gray-100 font-semibold">
                            <PlanName planCode={packageInfo.code} />
                        </h3>
                    </header>
                    <div className="text-gray-800 dark:text-gray-100 font-bold mb-4">
                        <span className="text-3xl">{getPrice(packageInfo)}</span>
                        <span className="text-2xl"> {currencySymbol}</span>
                        <span className="text-gray-500 font-medium text-sm">/{t('plan.month')}</span>
                        {isAnnual && packageInfo[monthlyPriceField] > packageInfo[yearlyPriceField] && (
                            <span className="ml-2 text-sm font-semibold text-green-600">
                                −{Math.round((1 - packageInfo[yearlyPriceField] / packageInfo[monthlyPriceField]) * 100)} %
                            </span>
                        )}
                    </div>
                    {activePackage?.id === packageInfo.id ? (
                        <button className="btn bg-indigo-500 text-white w-full">
                            {t('project.active')}
                        </button>
                    ) : (
                        <button className="btn bg-gray-900 text-white w-full" onClick={() => handleSelectPackage(packageInfo)}>
                            {t('project.select')}
                        </button>
                    )}
                </div>
                <div className="px-5 pt-4 pb-5">
                    <FeatureSectionTitle>{t('plan.feature_group.main', {defaultValue: 'Hlavní parametry'})}</FeatureSectionTitle>
                    <ul>
                        <FeatureRow label={t('plan.feature.users', {defaultValue: 'Počet uživatelů'})}>{t('plan.unlimited')}</FeatureRow>
                        {monthlyRevenueValue(packageInfo.code) && (
                            <FeatureRow label={t('plan.feature.monthly_revenue', {defaultValue: 'Měsíční tržby'})}>
                                <strong>{monthlyRevenueLabels[monthlyRevenueValue(packageInfo.code)]}</strong>
                            </FeatureRow>
                        )}
                        <FeatureRow label={t('plan.marketing-integrations-count', {defaultValue: 'Počet marketingových integrací'})}>
                            <strong>{(packageInfo.features?.max_marketing_integrations ?? packageInfo.maxIntegrations) < 0 ? t('plan.unlimited') : (packageInfo.features?.max_marketing_integrations ?? packageInfo.maxIntegrations)}</strong>
                        </FeatureRow>
                        <FeatureRow label={t('plan.free-domain-count')}>
                            {packageInfo.maxDomains === 0 ? t('plan.unlimited') : packageInfo.maxDomains}
                        </FeatureRow>
                        <FeatureRow label={t('plan.feature.retention', {defaultValue: 'Datová historie'})}>
                            {packageInfo.retentionMonths > 0
                                ? t('plan.feature_value.months', {defaultValue: '{{count}} měsíců', count: packageInfo.retentionMonths})
                                : t('plan.unlimited')}
                        </FeatureRow>
                        <FeatureRow label={t('plan.updates-count')}>
                            {packageInfo.dataUpdatesPerDay > 0 ? `${24 / packageInfo.dataUpdatesPerDay}h` : t('plan.unlimited')}
                        </FeatureRow>
                        <FeatureRow className="xl:min-h-12" label={t('plan.feature.marketing_performance', {defaultValue: 'Marketingový výkon'})}>{renderFeatureValue(packageInfo.features?.marketing_performance ?? 'channel_campaign')}</FeatureRow>
                    </ul>
                    {showAllFeatures && <>
                        <FeatureSectionTitle>{t('plan.feature_group.dashboards', {defaultValue: 'Dashboardy a analýzy'})}</FeatureSectionTitle>
                        <ul>
                            <FeatureRow label={t('plan.feature.daily_overview', {defaultValue: 'Denní výkon a celkové shrnutí'})}><FeatureYesNo question={true}/></FeatureRow>
                            <FeatureRow label={t('plan.feature.sales_orders_aov', {defaultValue: 'Tržby, objednávky, AOV'})}><FeatureYesNo question={true}/></FeatureRow>
                            <FeatureRow label={t('plan.feature.gross_margin_profit', {defaultValue: 'Hrubá marže a profit'})}><FeatureYesNo question={true}/></FeatureRow>
                            <FeatureRow label={t('plan.feature.top_products', {defaultValue: 'Top produkty'})}><FeatureYesNo question={true}/></FeatureRow>
                            <FeatureRow label={t('plan.feature.cancellations', {defaultValue: 'Storna'})}><FeatureYesNo question={true}/></FeatureRow>
                            <FeatureRow label={t('plan.feature.abc_analysis', {defaultValue: 'ABC analýza produktů'})}><FeatureYesNo question={packageInfo.features?.abc_analysis === true}/></FeatureRow>
                        </ul>
                        <FeatureSectionTitle>{t('plan.feature_group.features_permissions', {defaultValue: 'Funkce a oprávnění'})}</FeatureSectionTitle>
                        <ul>
                            <FeatureRow label={t('plan.feature.additional_cost_sources', {defaultValue: 'E-mailing, affiliate a ostatní náklady'})}>
                                <FeatureYesNo question={packageInfo.features?.additional_cost_sources === true
                                    || (
                                        packageInfo.features?.emailing === true
                                        && (packageInfo.features?.affiliate ?? packageInfo.allowAffiliateEvaluation) === true
                                        && packageInfo.features?.other_costs === true
                                    )}/>
                            </FeatureRow>
                            <FeatureRow label={t('plan.feature.roles_permissions', {defaultValue: 'Role a oprávnění'})}><FeatureYesNo question={packageInfo.features?.roles_permissions !== false}/></FeatureRow>
                        </ul>
                        <FeatureSectionTitle>{t('plan.feature_group.service', {defaultValue: 'Servis a onboarding'})}</FeatureSectionTitle>
                        <ul>
                            <FeatureRow className="xl:min-h-16" label={t('plan.feature.onboarding', {defaultValue: 'Onboarding'})}>{renderFeatureValue(packageInfo.features?.onboarding ?? 'self_service_review')}</FeatureRow>
                            <FeatureRow className="xl:min-h-16" label={t('plan.feature.setup_support', {defaultValue: 'Podpora k nastavení aplikace'})}>
                                <strong>{(packageInfo.features?.setup_support_minutes ?? 0) > 0
                                    ? t('plan.feature_value.setup_support_minutes', {defaultValue: 'Do {{count}} minut zdarma', count: packageInfo.features.setup_support_minutes})
                                    : t('plan.feature_value.no_free_support', {defaultValue: 'Bez podpory zdarma'})}</strong>
                            </FeatureRow>
                            <FeatureRow label={t('plan.feature.support', {defaultValue: 'Podpora'})}>{renderFeatureValue(packageInfo.features?.support ?? 'email')}</FeatureRow>
                            <FeatureRow className="xl:min-h-20" label={t('plan.feature.included_consulting', {defaultValue: 'Konzultace se specialistou v ceně balíčku'})}>{renderFeatureValue(packageInfo.features?.included_consulting ?? 'none')}</FeatureRow>
                            <FeatureRow className="xl:min-h-20" label={t('plan.feature.team_training', {defaultValue: 'Školení týmu'})}>{renderFeatureValue(packageInfo.features?.team_training ?? 'video_docs')}</FeatureRow>
                        </ul>
                    </>}
                    <button
                        type="button"
                        className="mt-4 flex w-full items-center justify-center gap-2 border-t border-slate-200 pt-4 text-sm font-semibold text-violet-600 hover:text-violet-800 dark:border-slate-700 dark:text-violet-400 dark:hover:text-violet-300"
                        aria-expanded={showAllFeatures}
                        onClick={() => setShowAllFeatures((expanded) => !expanded)}
                    >
                        {showAllFeatures
                            ? t('plan.show_less', {defaultValue: 'Skrýt podrobnosti'})
                            : t('plan.show_more', {defaultValue: 'Zobrazit všechny funkce'})}
                        <svg className={`h-4 w-4 transition-transform ${showAllFeatures ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
                        </svg>
                    </button>
                </div>
            </div>
        );
    };

    const FeatureYesNo = ({ question }) => (
        question ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-500 mr-2" viewBox="0 0 24 24">
                <path d="M5 12l5 5l10 -10" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
        ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-red-500 mr-2" viewBox="0 0 24 24">
                <path d="M6 6l12 12" stroke="currentColor" strokeWidth="2" fill="none" />
                <path d="M18 6l-12 12" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
        )
    );

    const FeatureRow = ({label, children, className = ''}) => (
        <li className={`grid grid-cols-[minmax(0,1fr)_minmax(7rem,0.9fr)] items-center gap-x-3 py-1 text-sm ${className}`}>
            <span className="min-w-0">{label}:</span>
            <span className="flex min-w-0 items-center justify-end text-right">{children}</span>
        </li>
    );

    const FeatureSectionTitle = ({children}) => (
        <h4 className="mb-2 mt-5 border-t border-slate-200 pt-5 text-sm font-semibold text-slate-800 first:mt-0 first:border-t-0 first:pt-0 dark:border-slate-700 dark:text-slate-100">
            {children}
        </h4>
    );

    const individualPackages = packages
        .filter((pkg) => !pkg.public)
        .sort((left, right) => left[isAnnual ? yearlyPriceField : monthlyPriceField] - right[isAnnual ? yearlyPriceField : monthlyPriceField]);
    const publicPackages = packages
        .filter((pkg) => pkg.public)
        .sort((left, right) => left[isAnnual ? yearlyPriceField : monthlyPriceField] - right[isAnnual ? yearlyPriceField : monthlyPriceField]);

    return (
        <WideSecureLayout title={t('route.subscription')}>
            <StripeCardModalWithElements
                isOpen={Boolean(setupIntentClientSecret?.length)}
                onClose={() => setSetupIntentClientSecret(null)}
                setupIntentClientSecret={setupIntentClientSecret}
                project={project}
                billingAccount={billingAccount}
                selectedPackage={selectedPackage}
                selectedSubscription={selectedSubscription}
                onPaymentSuccess={handlePaymentSuccess}
            />
            <BillingModal
                isOpen={isBillingModalOpen}
                onClose={() => setIsBillingModalOpen(false)}
                title={t('project.billing-data')}
            >
                <BillingDataForm
                    project={project}
                    onSave={(billingAccount) => {
                        setBillingAccount(billingAccount);
                        setIsBillingModalOpen(false);
                        updateProjectSubscription(selectedPackage);
                    }}
                />
            </BillingModal>
            <CurrentPlanBox project={project} currencySymbol={currencySymbol} onCancel={() => void cancelSubscription()} cancelling={cancelling}/>
            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                onConfirm={handleConfirmSelection}
                onClose={() => setIsConfirmationModalOpen(false)}
                project={project}
                packageInfo={selectedPackage}
                isAnnual={isAnnual}
                onBillingCycleChange={setIsAnnual}
                currencyCode={currencyCode}
            />
            <LoadingComponent loading={packagesLoading} className="min-h-48">
            <div className="mb-8">
                <div className="space-y-6">
                    <section>
                        <div className="mb-6 flex cursor-pointer items-center justify-center space-x-3" onClick={() => setIsAnnual(prev => !prev)}>
                            <div className={`text-sm ${!isAnnual ? 'text-gray-800 font-bold' : 'text-gray-500 font-medium'}`}>
                                {t('payment.monthly')}
                            </div>
                            <div className="form-switch">
                                <input
                                    type="checkbox"
                                    id="toggle"
                                    className="sr-only"
                                    checked={isAnnual}
                                    onChange={() => setIsAnnual(prev => !prev)}
                                />
                                <label className="bg-gray-400 dark:bg-gray-700" htmlFor="toggle">
                                    <span className="bg-white shadow-sm" aria-hidden="true"></span>
                                    <span className="sr-only">{t('payment.pay-annually')}</span>
                                </label>
                            </div>
                            <div className={`text-sm ${isAnnual ? 'text-gray-800 font-bold' : 'text-gray-500 font-medium'}`}>
                                {t('payment.annually')}
                            </div>
                        </div>
                        {individualPackages.length > 0 && <div className="mb-8">
                            <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">{t('subscription.individual_packages', {defaultValue: 'Váš aktuální a individuální balíček'})}</h2>
                            <div className="grid grid-cols-12 gap-6">
                                {individualPackages.map((pkg, index) => <PackageCard key={pkg.id} packageInfo={pkg} index={index}/>)}
                            </div>
                        </div>}
                        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">{t('subscription.public_packages', {defaultValue: 'Balíčky'})}</h2>
                        <div className="grid grid-cols-12 gap-6">
                            {publicPackages.map((pkg, index) => <PackageCard key={pkg.id} packageInfo={pkg} index={index + 1}/>)}
                        </div>
                        <div className="mt-6 flex flex-col gap-4 rounded-xl border border-violet-200 bg-violet-50 p-6 dark:border-violet-800 dark:bg-violet-950/30 md:flex-row md:items-center md:justify-between">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('subscription.enterprise_title', {defaultValue: 'Potřebujete individuální řešení?'})}</h2>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{t('subscription.enterprise_description', {defaultValue: 'Pro skupiny, holdingy, vlastní konektory a procesy připravíme řešení na míru.'})}</p>
                            </div>
                            <a className="btn whitespace-nowrap bg-violet-600 !text-white hover:bg-violet-700 hover:!text-white" href="mailto:info@datixo.com">{t('subscription.contact_datixo', {defaultValue: 'Kontaktovat Datixo'})}</a>
                        </div>
                    </section>
                </div>
            </div>
            </LoadingComponent>
        </WideSecureLayout>
    );
}

export default ProjectSubscription;
